/**
 * Registro de "pestañas que no dejan vencer la sesión".
 *
 * EL PROBLEMA QUE RESUELVE
 * ------------------------
 * La carga masiva (/shipment/bulk-upload*) es una pantalla de MONITOREO: el
 * operador la deja abierta mientras Django procesa el lote, muchas veces sin
 * tocarla. El `LogoutTimer` mide inactividad, así que a los 20 minutos avisaba y
 * a los 22 cerraba la sesión — justo encima del proceso que se estaba vigilando.
 *
 * Mientras HAYA AL MENOS UNA pestaña parada en carga masiva, la sesión no vence,
 * y eso vale para TODAS las pestañas: si hay 5 abiertas y solo la quinta está en
 * carga masiva, en ninguna de las otras cuatro aparece el modal de aviso.
 *
 * POR QUÉ localStorage Y NO REDUX
 * -------------------------------
 * redux-persist escribe en localStorage pero cada pestaña tiene su store en
 * memoria: se hidrata al cargar y después no se sincroniza. Para que una pestaña
 * sepa lo que hace otra hace falta el storage compartido. Es el mismo canal que
 * ya usa `LogoutTimer` para propagar la actividad entre pestañas.
 *
 * POR QUÉ ARRIENDOS CON VENCIMIENTO (TTL) Y NO UN SIMPLE CONTADOR
 * --------------------------------------------------------------
 * Cada pestaña renueva su entrada cada `KEEP_ALIVE_HEARTBEAT_MS`; una entrada sin
 * renovar por más de `KEEP_ALIVE_TTL_MS` se descarta. Un contador que se
 * incrementa al entrar y se decrementa al salir se rompe con lo que NO avisa: un
 * crash del navegador, un kill del proceso, un cierre de sesión del SO. Ese
 * contador quedaría en 1 para siempre y la sesión no vencería NUNCA en esa
 * máquina — exactamente el agujero de seguridad que el timer existe para tapar.
 * Con TTL, una pestaña que muere deja de renovar y su arriendo se cae solo.
 *
 * El TTL es holgado a propósito: Chrome estrangula los timers de pestañas en
 * segundo plano a ~1 tick por minuto, así que un TTL corto mataría el arriendo de
 * una pestaña viva pero oculta. 30 s de latido contra 3 min de TTL aguanta ese
 * estrangulamiento con margen.
 *
 * Todo va envuelto en try/catch: `localStorage` tira en modo privado, con la
 * cuota llena o si el navegador lo bloquea. Que no se pueda leer el registro no
 * puede romper la app — en ese caso simplemente no hay arriendo y el timer se
 * comporta como siempre.
 */

export const KEEP_ALIVE_STORAGE_KEY = 'session:keep-alive'

/** Cada cuánto renueva su arriendo una pestaña en carga masiva. */
export const KEEP_ALIVE_HEARTBEAT_MS = 30 * 1000

/** Sin renovar por más de esto, el arriendo se considera muerto. */
export const KEEP_ALIVE_TTL_MS = 3 * 60 * 1000

const TAB_ID_KEY = 'session:keep-alive:tab-id'

/** `{ [tabId]: epoch ms del último latido }` */
type LeaseStore = Record<string, number>

const readStore = (): LeaseStore => {
  try {
    const raw = localStorage.getItem(KEEP_ALIVE_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    // Se filtran valores no numéricos: una entrada corrupta con `undefined`
    // nunca vencería y dejaría la sesión abierta para siempre.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        ([, at]) => typeof at === 'number' && Number.isFinite(at)
      )
    ) as LeaseStore
  } catch {
    return {}
  }
}

const writeStore = (store: LeaseStore): void => {
  try {
    if (Object.keys(store).length === 0) {
      localStorage.removeItem(KEEP_ALIVE_STORAGE_KEY)
      return
    }
    localStorage.setItem(KEEP_ALIVE_STORAGE_KEY, JSON.stringify(store))
  } catch {
    // Sin persistencia no hay arriendo y la sesión vence como siempre. Es el
    // fallo seguro: se pierde la comodidad, no el control de sesión.
  }
}

const prune = (store: LeaseStore, now: number): LeaseStore =>
  Object.fromEntries(
    Object.entries(store).filter(([, at]) => now - at < KEEP_ALIVE_TTL_MS)
  )

/**
 * Id estable de ESTA pestaña. Vive en `sessionStorage`, que es por pestaña y
 * sobrevive al F5: al recargar, la pestaña reclama su propio arriendo en vez de
 * abrir uno nuevo y dejar el anterior colgando hasta que venza.
 */
export function getKeepAliveTabId(): string {
  try {
    const existing = sessionStorage.getItem(TAB_ID_KEY)
    if (existing) return existing
    const id = `${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 10)}`
    sessionStorage.setItem(TAB_ID_KEY, id)
    return id
  } catch {
    // Sin sessionStorage el id no sobrevive al reload; el arriendo viejo queda
    // huérfano y se cae solo por TTL. Aceptable.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

/** Ids de las pestañas con arriendo vigente (útil para tests y depuración). */
export function getSessionKeepAliveTabs(now: number = Date.now()): string[] {
  return Object.keys(prune(readStore(), now))
}

/** ¿Hay alguna pestaña (esta u otra) exigiendo que la sesión siga viva? */
export function isSessionKeepAliveActive(now: number = Date.now()): boolean {
  return getSessionKeepAliveTabs(now).length > 0
}

/** Renueva (o abre) el arriendo de esta pestaña y limpia los vencidos. */
export function renewSessionKeepAlive(
  tabId: string,
  now: number = Date.now()
): void {
  const store = prune(readStore(), now)
  store[tabId] = now
  writeStore(store)
}

/** Suelta el arriendo de esta pestaña. Al soltar el último, el timer normal vuelve a correr. */
export function releaseSessionKeepAlive(tabId: string): void {
  const store = readStore()
  if (!(tabId in store)) return
  delete store[tabId]
  writeStore(store)
}
